import { useEffect } from "react";
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/shopify";

export default function CartDrawer() {
  const {
    cart,
    drawerOpen,
    closeDrawer,
    updateQty,
    removeItem,
    goToCheckout,
    mutating,
  } = useCart();

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [drawerOpen]);

  const lines = cart?.lines?.edges?.map((e) => e.node) || [];
  const subtotal = cart?.cost?.subtotalAmount;
  const total = cart?.cost?.totalAmount;

  return (
    <>
      {/* Overlay */}
      <div
        onClick={closeDrawer}
        aria-hidden="true"
        className={`fixed inset-0 z-[90] bg-navy-deep/60 backdrop-blur-sm transition-opacity duration-300 ${
          drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* Drawer */}
      <aside
        role="dialog"
        aria-label="Shopping cart"
        aria-modal={drawerOpen}
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[91] h-full w-full sm:w-[440px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <header className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <ShoppingBag size={20} className="text-green-brand" />
            <h2 className="font-display tracking-widest uppercase text-navy text-base">
              Your Cart
            </h2>
            {cart?.totalQuantity > 0 && (
              <span className="text-xs bg-green-brand text-white font-semibold px-2 py-0.5 rounded-full">
                {cart.totalQuantity}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            aria-label="Close cart"
            className="h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-gray-soft text-navy"
            data-testid="cart-close"
          >
            <X size={20} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {lines.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-8 py-16 text-navy-deep/70">
              <div className="h-16 w-16 rounded-full bg-gray-soft flex items-center justify-center">
                <ShoppingBag size={28} />
              </div>
              <p className="font-display tracking-widest text-sm uppercase text-navy mt-5">
                Your cart is empty
              </p>
              <p className="mt-2 text-sm">
                Browse our store and add some Veterans for Indiana gear.
              </p>
              <button
                type="button"
                onClick={closeDrawer}
                className="btn btn-secondary mt-6"
                data-testid="cart-keep-shopping"
              >
                Keep Shopping
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {lines.map((line) => {
                const m = line.merchandise;
                const img = m.image || m.product?.featuredImage;
                const optionsLabel = m.selectedOptions
                  ?.filter(
                    (o) => !(o.name === "Title" && o.value === "Default Title")
                  )
                  ?.map((o) => `${o.name}: ${o.value}`)
                  ?.join(" · ");
                return (
                  <li
                    key={line.id}
                    className="p-5 flex gap-4"
                    data-testid={`cart-line-${line.id}`}
                  >
                    <div className="h-20 w-20 rounded-md bg-cream overflow-hidden flex-shrink-0">
                      {img && (
                        <img
                          src={img.url}
                          alt={img.altText || m.product?.title || ""}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display tracking-wide text-navy text-sm leading-snug">
                        {m.product?.title}
                      </p>
                      {optionsLabel && (
                        <p className="mt-0.5 text-xs text-navy-deep/65">
                          {optionsLabel}
                        </p>
                      )}
                      <p className="mt-2 text-sm font-semibold text-navy">
                        {formatPrice(m.price)}
                      </p>
                      <div className="mt-3 flex items-center gap-3">
                        <div className="inline-flex items-center ring-1 ring-gray-300 rounded-md">
                          <button
                            type="button"
                            onClick={() =>
                              line.quantity > 1
                                ? updateQty(line.id, line.quantity - 1)
                                : removeItem(line.id)
                            }
                            disabled={mutating}
                            aria-label="Decrease quantity"
                            className="h-8 w-8 inline-flex items-center justify-center hover:bg-gray-soft disabled:opacity-50"
                            data-testid={`cart-line-dec-${line.id}`}
                          >
                            <Minus size={14} />
                          </button>
                          <span className="px-3 text-sm font-medium text-navy min-w-[1.5rem] text-center">
                            {line.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => updateQty(line.id, line.quantity + 1)}
                            disabled={mutating}
                            aria-label="Increase quantity"
                            className="h-8 w-8 inline-flex items-center justify-center hover:bg-gray-soft disabled:opacity-50"
                            data-testid={`cart-line-inc-${line.id}`}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(line.id)}
                          disabled={mutating}
                          aria-label="Remove item"
                          className="text-navy-deep/60 hover:text-red-600 disabled:opacity-50"
                          data-testid={`cart-line-remove-${line.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {lines.length > 0 && (
          <footer className="border-t border-gray-200 p-5 bg-gray-soft/40">
            <div className="flex items-center justify-between text-sm text-navy-deep/80">
              <span>Subtotal</span>
              <span className="font-semibold text-navy">
                {formatPrice(subtotal)}
              </span>
            </div>
            <p className="mt-1 text-xs text-navy-deep/60">
              Taxes and shipping calculated at checkout.
            </p>
            <button
              type="button"
              onClick={goToCheckout}
              disabled={mutating || !cart?.checkoutUrl}
              className="btn btn-primary w-full justify-center mt-4 disabled:opacity-60"
              data-testid="cart-checkout-button"
            >
              Checkout · {formatPrice(total)} <ArrowRight size={18} />
            </button>
          </footer>
        )}
      </aside>
    </>
  );
}
